import { useEffect, useState } from "react";
import { api, formatApiErrorDetail } from "@/lib/api";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "@/components/ui/dialog";
import { toast } from "sonner";
import { Trash2 } from "lucide-react";

export default function EventFormDialog({ open, onOpenChange, event, mailbox, defaultDate, onSaved }) {
  const [subject, setSubject] = useState("");
  const [start, setStart] = useState("");
  const [end, setEnd] = useState("");
  const [location, setLocation] = useState("");
  const [attendees, setAttendees] = useState("");
  const [body, setBody] = useState("");
  const [saving, setSaving] = useState(false);
  const [deleting, setDeleting] = useState(false);

  useEffect(() => {
    if (!open) return;
    if (event) {
      setSubject(event.subject || "");
      setStart((event.start?.dateTime || "").slice(0, 16));
      setEnd((event.end?.dateTime || "").slice(0, 16));
      setLocation(event.location?.displayName || "");
      setAttendees((event.attendees || []).map((a) => a.emailAddress?.address).filter(Boolean).join(", "));
      setBody(event.bodyPreview || "");
    } else {
      const d = defaultDate || new Date().toISOString().slice(0, 10);
      setSubject(""); setStart(`${d}T09:00`); setEnd(`${d}T10:00`);
      setLocation(""); setAttendees(""); setBody("");
    }
  }, [open, event, defaultDate]);

  const save = async () => {
    if (!subject.trim()) { toast.error("Add an event title"); return; }
    if (!start || !end) { toast.error("Set start and end times"); return; }
    setSaving(true);
    const payload = {
      mailbox, subject, start: `${start}:00`, end: `${end}:00`, location, body,
      attendees: attendees.split(",").map((s) => s.trim()).filter(Boolean),
    };
    try {
      if (event) await api.patch(`/calendar/events/${event.id}`, payload);
      else await api.post("/calendar/events", payload);
      toast.success(event ? "Event updated" : "Event created");
      onOpenChange(false);
      onSaved?.();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Could not save event");
    } finally { setSaving(false); }
  };

  const remove = async () => {
    setDeleting(true);
    try {
      await api.delete(`/calendar/events/${event.id}`, { params: { mailbox } });
      toast.success("Event deleted");
      onOpenChange(false);
      onSaved?.();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Could not delete event");
    } finally { setDeleting(false); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md rounded-sm" data-testid="event-form-dialog">
        <DialogHeader>
          <DialogTitle className="font-heading">{event ? "Edit event" : "New event"}</DialogTitle>
          <DialogDescription className="text-xs text-gray-400">Times are in UTC.</DialogDescription>
        </DialogHeader>

        <label className="block text-sm font-semibold text-gray-700 mb-1">Title</label>
        <input data-testid="event-subject-input" value={subject} onChange={(e) => setSubject(e.target.value)}
          className="w-full border border-gray-300 rounded-sm px-3 py-2 text-sm mb-3 focus:outline-none focus:ring-2 focus:ring-[#002FA7]" />

        <div className="grid grid-cols-2 gap-3 mb-3">
          <div>
            <label className="block text-sm font-semibold text-gray-700 mb-1">Start</label>
            <input type="datetime-local" data-testid="event-start-input" value={start} onChange={(e) => setStart(e.target.value)}
              className="w-full border border-gray-300 rounded-sm px-2 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#002FA7]" />
          </div>
          <div>
            <label className="block text-sm font-semibold text-gray-700 mb-1">End</label>
            <input type="datetime-local" data-testid="event-end-input" value={end} onChange={(e) => setEnd(e.target.value)}
              className="w-full border border-gray-300 rounded-sm px-2 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#002FA7]" />
          </div>
        </div>

        <label className="block text-sm font-semibold text-gray-700 mb-1">Location</label>
        <input data-testid="event-location-input" value={location} onChange={(e) => setLocation(e.target.value)}
          className="w-full border border-gray-300 rounded-sm px-3 py-2 text-sm mb-3 focus:outline-none focus:ring-2 focus:ring-[#002FA7]" />

        <label className="block text-sm font-semibold text-gray-700 mb-1">Attendees (comma-separated emails)</label>
        <input data-testid="event-attendees-input" value={attendees} onChange={(e) => setAttendees(e.target.value)}
          className="w-full border border-gray-300 rounded-sm px-3 py-2 text-sm mb-3 focus:outline-none focus:ring-2 focus:ring-[#002FA7]" />

        <label className="block text-sm font-semibold text-gray-700 mb-1">Notes</label>
        <textarea data-testid="event-body-input" value={body} onChange={(e) => setBody(e.target.value)} rows={2}
          className="w-full border border-gray-300 rounded-sm px-3 py-2 text-sm mb-2 focus:outline-none focus:ring-2 focus:ring-[#002FA7]" />

        <DialogFooter className="gap-2">
          {event && (
            <button onClick={remove} disabled={deleting} data-testid="event-delete-button"
              className="mr-auto flex items-center gap-1.5 text-red-600 hover:bg-red-50 px-3 py-2 rounded-sm font-semibold text-sm">
              <Trash2 className="w-4 h-4" /> Delete
            </button>
          )}
          <button onClick={() => onOpenChange(false)} className="bg-white border border-gray-200 text-gray-700 hover:bg-gray-50 px-4 py-2 rounded-sm font-semibold text-sm">Cancel</button>
          <button onClick={save} disabled={saving} data-testid="event-save-button"
            className="bg-[#002FA7] hover:bg-[#002480] text-white px-4 py-2 rounded-sm font-semibold text-sm disabled:opacity-60">
            {saving ? "Saving…" : event ? "Save" : "Create"}
          </button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
